import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Divider,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { format, addDays, eachDayOfInterval, getDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { API_BASE_URL, authFetch, responseErrorText } from '../../../config/api.config';

interface BulkSlotCreatorProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  tourType: string;
  minParticipants: number;
  maxParticipants: number;
  basePrice: number;
}

interface BulkCreateForm {
  dateFrom: string;
  dateTo: string;
  weekDays: number[];
  timeSlots: TimeSlot[];
}

interface TourType {
  id: number;
  code: string;
  name: string;
  description?: string;
  basePricePerPerson: number;
  minParticipants: number;
  maxParticipants: number;
  isActive: boolean;
  colorCode?: string;
}

const WEEKDAYS = [
  { value: 0, label: 'Sonntag', short: 'So' },
  { value: 1, label: 'Montag', short: 'Mo' },
  { value: 2, label: 'Dienstag', short: 'Di' },
  { value: 3, label: 'Mittwoch', short: 'Mi' },
  { value: 4, label: 'Donnerstag', short: 'Do' },
  { value: 5, label: 'Freitag', short: 'Fr' },
  { value: 6, label: 'Samstag', short: 'Sa' },
];

const BulkSlotCreator: React.FC<BulkSlotCreatorProps> = ({ open, onClose, onSuccess }) => {
  const [tourTypes, setTourTypes] = useState<TourType[]>([]);
  const [loadingTourTypes, setLoadingTourTypes] = useState(false);

  const [formData, setFormData] = useState<BulkCreateForm>({
    dateFrom: format(new Date(), 'yyyy-MM-dd'),
    dateTo: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    weekDays: [1, 2, 3, 4, 5], // Monday to Friday
    timeSlots: [],
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load tour types when dialog opens
  useEffect(() => {
    if (open) {
      loadTourTypes();
    }
  }, [open]);

  // Initialize time slots when tour types are loaded
  useEffect(() => {
    if (tourTypes.length > 0 && formData.timeSlots.length === 0) {
      // Create default time slots using the first two tour types
      const defaultSlots: TimeSlot[] = [];

      if (tourTypes[0]) {
        defaultSlots.push({
          startTime: '11:00',
          endTime: '13:30',
          tourType: tourTypes[0].code,
          minParticipants: tourTypes[0].minParticipants,
          maxParticipants: tourTypes[0].maxParticipants,
          basePrice: tourTypes[0].basePricePerPerson,
        });
      }

      if (tourTypes[1]) {
        defaultSlots.push({
          startTime: '14:00',
          endTime: '16:30',
          tourType: tourTypes[1].code,
          minParticipants: tourTypes[1].minParticipants,
          maxParticipants: tourTypes[1].maxParticipants,
          basePrice: tourTypes[1].basePricePerPerson,
        });
      }

      setFormData(prev => ({
        ...prev,
        timeSlots: defaultSlots,
      }));
    }
  }, [tourTypes, formData.timeSlots.length]);

  const loadTourTypes = async () => {
    setLoadingTourTypes(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/tour-types`, {});

      if (!response.ok) throw new Error(await responseErrorText(response));

      const data: TourType[] = await response.json();
      const activeTourTypes = data.filter(tt => tt.isActive).sort((a, b) => a.code.localeCompare(b.code));
      setTourTypes(activeTourTypes);
    } catch (err: any) {
      console.error('Error loading tour types:', err);
      setError('Fehler beim Laden der Tour-Typen');
    } finally {
      setLoadingTourTypes(false);
    }
  };

  // Handle date change
  const handleDateChange = (field: 'dateFrom' | 'dateTo') => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  // Handle weekday toggle
  const handleWeekDayToggle = (day: number) => {
    setFormData(prev => ({
      ...prev,
      weekDays: prev.weekDays.includes(day)
        ? prev.weekDays.filter(d => d !== day)
        : [...prev.weekDays, day].sort(),
    }));
  };

  // Add time slot
  const handleAddTimeSlot = () => {
    // Use first tour type as default
    const defaultTourType = tourTypes[0] || {
      code: '',
      basePricePerPerson: 0,
      minParticipants: 30,
      maxParticipants: 140
    };

    const newSlot: TimeSlot = {
      startTime: '10:00',
      endTime: '12:30',
      tourType: defaultTourType.code,
      minParticipants: defaultTourType.minParticipants,
      maxParticipants: defaultTourType.maxParticipants,
      basePrice: defaultTourType.basePricePerPerson,
    };
    setFormData(prev => ({
      ...prev,
      timeSlots: [...prev.timeSlots, newSlot],
    }));
  };

  // Remove time slot
  const handleRemoveTimeSlot = (index: number) => {
    setFormData(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.filter((_, i) => i !== index),
    }));
  };

  // Update time slot
  const handleTimeSlotChange = (index: number, field: keyof TimeSlot) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.map((slot, i) => 
        i === index ? { ...slot, [field]: value } : slot
      ),
    }));
  };

  // Calculate number of slots to be created
  const calculateSlotsCount = (): number => {
    if (!formData.dateFrom || !formData.dateTo || formData.weekDays.length === 0) {
      return 0;
    }

    const days = eachDayOfInterval({
      start: new Date(formData.dateFrom),
      end: new Date(formData.dateTo),
    });

    const matchingDays = days.filter(day => formData.weekDays.includes(getDay(day)));
    return matchingDays.length * formData.timeSlots.length;
  };

  // Handle form submission
  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Prepare the bulk creation payload matching backend DTO
      // We need to send multiple requests for different tour types
      let totalCreated = 0;
      const errors: string[] = [];

      for (const slot of formData.timeSlots) {
        const payload = {
          startDate: formData.dateFrom,
          endDate: formData.dateTo,
          times: [`${slot.startTime}:00`],
          daysOfWeek: formData.weekDays,
          tourType: slot.tourType,
          maxCapacity: slot.maxParticipants,
          minCapacity: slot.minParticipants,
          pricePerPerson: slot.basePrice,
          isAvailable: true,
          specialNotes: '',
          skipExisting: true
        };

        // FIX 24.08.2026 (Kundin-Befund 23./25.08., Oktober-Termine): authFetch mit
        // automatischem Token-Refresh. Vorher lief der Request nach 15 Minuten in ein
        // 401 mit leerem Body, die Fehlermeldung war "FLOSSFAHRT 14:30:" ohne Grund
        // (statusText ist unter HTTP/2 immer leer) und es wurden keine Termine angelegt.
        const response = await authFetch(`${API_BASE_URL}/calendar/bulk`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          errors.push(`${slot.tourType} ${slot.startTime}: ${await responseErrorText(response)}`);
        } else {
          const result = await response.json();
          totalCreated += Array.isArray(result) ? result.length : 1;
        }
      }

      if (errors.length > 0) {
        setError(`Einige Termine konnten nicht erstellt werden:\n${errors.join('\n')}`);
      }

      if (totalCreated > 0) {
        setSuccessMessage(`Erfolgreich ${totalCreated} Termine erstellt!`);

        // Wait a moment to show success message
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (err: any) {
      console.error('Bulk creation error:', err);
      setError(err.message || 'Fehler beim Erstellen der Termine');
    } finally {
      setSaving(false);
    }
  };

  // Handle tour type change and update price
  const handleTourTypeChange = (index: number) => (event: any) => {
    const tourTypeCode = event.target.value;
    const tourTypeInfo = tourTypes.find(t => t.code === tourTypeCode);

    setFormData(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.map((slot, i) =>
        i === index
          ? {
              ...slot,
              tourType: tourTypeCode,
              basePrice: tourTypeInfo?.basePricePerPerson || slot.basePrice,
              minParticipants: tourTypeInfo?.minParticipants || slot.minParticipants,
              maxParticipants: tourTypeInfo?.maxParticipants || slot.maxParticipants
            }
          : slot
      ),
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Termine in großen Mengen erstellen</Typography>
        <Typography variant="body2" color="textSecondary">
          Erstellen Sie mehrere Termine auf einmal für einen Zeitraum
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {/* Date Range */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            📅 Zeitraum
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Von Datum"
                type="date"
                value={formData.dateFrom}
                onChange={handleDateChange('dateFrom')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Bis Datum"
                type="date"
                value={formData.dateTo}
                onChange={handleDateChange('dateTo')}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: formData.dateFrom,
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Weekdays Selection */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            📆 Wochentage
          </Typography>
          <FormGroup row>
            {WEEKDAYS.map(day => (
              <FormControlLabel
                key={day.value}
                control={
                  <Checkbox
                    checked={formData.weekDays.includes(day.value)}
                    onChange={() => handleWeekDayToggle(day.value)}
                  />
                }
                label={day.label}
              />
            ))}
          </FormGroup>
          <Box sx={{ mt: 1 }}>
            {formData.weekDays.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {formData.weekDays.sort().map(dayValue => {
                  const day = WEEKDAYS.find(d => d.value === dayValue);
                  return (
                    <Chip
                      key={dayValue}
                      label={day?.short}
                      size="small"
                      color="primary"
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        </Paper>

        {/* Time Slots */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1">
              ⏰ Zeitslots ({formData.timeSlots.length})
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddTimeSlot}
              variant="outlined"
              size="small"
            >
              Zeitslot hinzufügen
            </Button>
          </Box>

          <List>
            {formData.timeSlots.map((slot, index) => (
              <ListItem key={index} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                <Box sx={{ width: '100%' }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <TextField
                        label="Startzeit"
                        type="time"
                        value={slot.startTime}
                        onChange={handleTimeSlotChange(index, 'startTime')}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <TextField
                        label="Endzeit"
                        type="time"
                        value={slot.endTime}
                        onChange={handleTimeSlotChange(index, 'endTime')}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Tour-Typ</InputLabel>
                        <Select
                          value={slot.tourType}
                          label="Tour-Typ"
                          onChange={handleTourTypeChange(index)}
                          disabled={loadingTourTypes || tourTypes.length === 0}
                        >
                          {tourTypes.map(type => (
                            <MenuItem key={type.id} value={type.code}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <TextField
                        label="Min"
                        type="number"
                        value={slot.minParticipants}
                        onChange={handleTimeSlotChange(index, 'minParticipants')}
                        fullWidth
                        size="small"
                        inputProps={{ min: 1, max: 160 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <TextField
                        label="Max"
                        type="number"
                        value={slot.maxParticipants}
                        onChange={handleTimeSlotChange(index, 'maxParticipants')}
                        fullWidth
                        size="small"
                        inputProps={{ min: 1, max: 160 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <TextField
                        label="Preis"
                        type="number"
                        value={slot.basePrice}
                        onChange={handleTimeSlotChange(index, 'basePrice')}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 1, sm: 1 }}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveTimeSlot(index)}
                        disabled={formData.timeSlots.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Summary */}
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Zusammenfassung:</strong> Es werden <strong>{calculateSlotsCount()}</strong> Termine erstellt
          </Typography>
          {calculateSlotsCount() > 0 && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {formData.weekDays.length} Wochentage × {formData.timeSlots.length} Zeitslots × {Math.ceil(
                (new Date(formData.dateTo).getTime() - new Date(formData.dateFrom).getTime()) / (1000 * 60 * 60 * 24 * 7)
              )} Wochen
            </Typography>
          )}
        </Alert>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CancelIcon />} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          disabled={saving || formData.timeSlots.length === 0 || formData.weekDays.length === 0}
        >
          {saving ? `Erstelle ${calculateSlotsCount()} Termine...` : `${calculateSlotsCount()} Termine erstellen`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(BulkSlotCreator);